import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import { Theme, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import parse from 'html-react-parser';
import Image from 'next/image';
import React, { useEffect, useRef } from 'react';
import { TokenDetails } from '@/layouts/HomeLayout';
import { isVideo } from '@/utils/isVideo.utils';
import { SoldItemCountDetails } from '@/lib/providers/NftContextProvider';
import { Videos } from '@/assets/videos';

export const parseHeroDescription = (description?: string, hyberLinkTitle?: string, theme?: Theme) => {
  if (!description) return '';

  let formattedDescription = description.replace(/\n/g, '<br />');

  const styles = `
      color: ${theme?.MojitoClaim?.linkColor};
      font-size: 16px;
      font-family: ${theme?.MojitoClaim?.font?.tertiary};
      font-weight: 400;
      line-height: 140%;
      letter-spacing: -0.14px;
      font-style: normal;
      text-decoration: underline;
      cursor: pointer;
    `;

  const urlRegex = /(https?:\/\/[^\s]+)/g;

  formattedDescription = formattedDescription.replace(urlRegex, (url) => {
    return `<a href="${url}" target="_blank" rel="noopener noreferrer" style="${styles}">${hyberLinkTitle}</a>`;
  });

  return formattedDescription;
};

export interface HeroBlockProps {
  isSaleStarted: boolean;
  isSaleEnded: boolean;
  title: string;
  subTitle: string;
  hyberLinkTitle?: string;
  description: string;
  imageURL: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  tokenDetails: TokenDetails;
  soldItems: SoldItemCountDetails[];
}
const HeroBlock: React.FC<HeroBlockProps> = ({
  isSaleStarted,
  isSaleEnded,
  title,
  subTitle,
  description,
  imageURL,
  tokenDetails,
  hyberLinkTitle,
  soldItems
}) => {
  const theme = useTheme();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (videoRef) {
      videoRef.current?.load();
    }
  }, []);

  return (
    <Box
      sx={{
        fontWeight: 500,
        fontSize: '16px',
        padding: {
          lg: '0px 120px 40px 120px',
          md: '0px 120px 40px 120px',
          xs: '16px 0 0 0'
        },
        marginTop: isMobile ? '0px' : '80px',
        textAlign: isMobile ? 'center' : 'left',
        backgroundColor: theme?.palette?.background?.paper
      }}
    >
      <Container maxWidth="lg">
        {isMobile && (
          <Grid container>
            <Grid
              item
              xs={12}
              lg={6}
              sx={{
                display: 'flex',
                justifyContent: {
                  lg: 'flex-end',
                  md: 'flex-end',
                  xs: 'flex-start'
                },
                padding: {
                  lg: 0,
                  md: 0,
                  xs: '0px 0px 30px'
                }
              }}
            >
              <video
                ref={videoRef}
                controls={false}
                loop
                muted
                playsInline
                autoPlay={true}
                style={{
                  width: '100%',
                  height: '100%'
                }}
              >
                <source src={Videos.KINETEK_HERO} />
              </video>
            </Grid>
          </Grid>
        )}
        <Grid container spacing={3}>
          <Grid item xs={12} lg={6} md={6} sm={6}>
            <Box>
              <Typography
                sx={{
                  fontWeight: 500
                }}
              >
                {subTitle}
              </Typography>
              <Typography
                sx={{
                  fontFamily: theme?.MojitoClaim?.font?.primary,
                  fontSize: isMobile ? '32px' : '48px',
                  lineHeight: isMobile ? '38px' : '120%',
                  fontStyle: 'normal',
                  fontWeight: 500,
                  textAlign: 'left',
                  color: theme?.palette?.text?.primary
                }}
              >
                {parse(title)}
              </Typography>
              <Typography
                sx={{
                  padding: isMobile ? '24px 0px 0px' : '24px 0px',
                  marginBottom: isMobile ? '18px' : '0px',
                  color: theme?.ClaimTemplate?.collectionHeaderColor,
                  fontFamily: theme?.MojitoClaim?.font?.secondary,
                  fontSize: '16px',
                  fontStyle: 'normal',
                  textAlign: 'left',
                  fontWeight: 500,
                  '& a': {
                    color: theme?.ClaimTemplate?.collectionHeaderColor,
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }
                }}
              >
                <span
                  dangerouslySetInnerHTML={{
                    __html: parseHeroDescription(description, hyberLinkTitle, theme)
                  }}
                />
              </Typography>
              {/* {isSaleStarted && !isSaleEnded} */}
            </Box>
          </Grid>
          {!isMobile && (
            <Grid
              item
              xs={12}
              lg={6}
              md={6}
              sm={6}
              sx={{
                display: 'flex',
                justifyContent: {
                  lg: 'flex-end',
                  md: 'flex-end',
                  xs: 'flex-start'
                },
                padding: {
                  lg: '0 0 0 40px !important',
                  md: '0 0 0 40px !important',
                  xs: '40px 0px'
                }
              }}
            >
              {isVideo(Videos.KINETEK_HERO) ? (
                <video
                  ref={videoRef}
                  controls={false}
                  loop
                  muted
                  playsInline
                  autoPlay={true}
                  style={{
                    width: '100%',
                    height: '100%'
                  }}
                >
                  <source src={Videos.KINETEK_HERO} />
                </video>
              ) : (
                <Box
                  sx={{
                    maxWidth: {
                      lg: '500px',
                      md: '100%'
                    },
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                >
                  <Image
                    unoptimized
                    style={{
                      maxWidth: '500px',
                      width: '100%',
                      height: 'auto',
                      maxHeight: '434px',
                      borderRadius: '10px'
                    }}
                    width={300}
                    height={434}
                    src={Videos.KINETEK_HERO}
                    alt="hero block"
                  />
                </Box>
              )}
            </Grid>
          )}
        </Grid>
      </Container>
    </Box>
  );
};

export default HeroBlock;
